import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { PiInfo } from "react-icons/pi";
import { MdCheckCircle } from "react-icons/md";

import FileUploader from "../shared/TailwindComponents/FileUploader";
import Input from "../shared/TailwindComponents/Input";
import Dropdown, { DropdownOption } from "../shared/TailwindComponents/Dropdown";
import { FolderProps } from "./documents/types";
import Button from "../shared/TailwindComponents/Button";
import { useHistory } from "react-router-dom";
import { RootState, useAppDispatch } from "../../store";
import { useSelector } from "react-redux";
import { importFilesSSE } from "../../store/actions/FolderAction";
import SSECreationProgress from "../shared/SSECreationProgress";
import {  FILE_IMPORT_RESET } from "../../store/types";
import { ShareUser } from "../shared/ShareAccessManager";
import CollaboratorSelector, { Collaborator } from "../shared/CollaboratorSelector";

// Import validation system
import { createFolderSchema, sanitizeString } from "../../utils/validation";
import { FieldError } from "../shared/FormComponents";

type Props = {
    onClose: () => void;
    existingFolders: FolderProps[];
    selectedFolderItem?: any;
    handleCreateFolder: (data: {
        folderId?: string;
        folderName?: string;
        files: File[];

    }) => void | Promise<void>;
    employees: ShareUser[]
    initialShared: ShareUser[]
    onRefresh?: (createdFolderId?: string) => void | Promise<void>;
};

const CreateFolder: React.FC<Props> = ({
    onClose,
    existingFolders,
    selectedFolderItem,
    employees,
    initialShared,
    onRefresh
}) => {
    const { t } = useTranslation();
    const [toggle, setToggle] = useState('existing')
    const history = useHistory()
    const dispatch = useAppDispatch()
    const userInfo = useSelector((state: RootState) => state.user.userInfo);
    const { isConnecting, connected, steps, ok, summary, complete } = useSelector((state: RootState) => state.folders)

    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [recievedFolderID, setRecievedFolderID] = useState(null)
    const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
    const [showPathTooltip, setShowPathTooltip] = useState(false);
    const [selectedFolderId, setSelectedFolderId] = useState<string>("");

    useEffect(() => {
        const initialCollaborators = initialShared.map(user => ({
            user_id: user.id,
            access: 'edit' as const
        }));
        setCollaborators(initialCollaborators);
    },[initialShared])

    useEffect(() => {
        setResult(summary)
        if (summary && summary.folderId) {
            setRecievedFolderID(summary.folderId)
        }
        if (summary && onRefresh) {
            // Pass the folder ID to the refresh callback for proper navigation
            if (summary.folderId) {
              onRefresh(summary.folderId);
            } else {
              onRefresh();
            }
        }
    }, [summary, onRefresh])

    const folderOptions = useMemo(() => {
        const options =
            (existingFolders || []).filter(f => f.FolderType !== 'Locked').map((f) => ({
                label:
                    (f as any).FolderName ??
                    (f as any).name ??
                    (f as any).title ??
                    String(f.id ?? f.FolderID ?? ""),
                value: String(f.id ?? f.FolderID ?? ""),
            })) || [];

        if (
            selectedFolderItem && options.length >= 0
        ) {
            options.push({
                label: selectedFolderItem.FolderName,
                value: selectedFolderItem.FolderID,
            });
        }

        return options;
    }, [existingFolders, selectedFolderItem]);

    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            FolderId: Yup.string().optional(),
            FolderName: Yup.string().trim().optional(),
            Files: Yup.array()
        }).test(
            "one-of-two",
            t("Please select an existing folder or enter a new folder name."),
            (values: any) => Boolean(values?.FolderId || values?.FolderName)
        );
    }, [t]);

    const formik = useFormik({
        initialValues: {
            FolderId: (selectedFolderItem && selectedFolderItem?.FolderID) || "",
            FolderName: "",
            Files: []
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setIsSubmitting(true);
                setResult(null);

                // Sanitize folder name
                const sanitizedFolderName = values.FolderName
                  ? sanitizeString(values.FolderName, 255, false)
                  : '';

                // Prepare collaborators with access levels
                const validCollaborators = collaborators
                    .map(c => ({
                        user_id: parseInt(String(c.user_id), 10),
                        access: c.access
                    }))
                    .filter(c => !Number.isNaN(c.user_id));

                await dispatch(
                    importFilesSSE(
                        {
                            FolderID: values.FolderId,
                            FolderName: sanitizedFolderName,
                            collaborators: validCollaborators.length > 0 ? validCollaborators : undefined
                        },
                        values.Files
                    )
                );
            } catch (e: any) {
                setError(e?.message || "Network error");
            } finally {
                setIsSubmitting(false);
            }
        },

    });
    const handleClose = async () => {
        if (recievedFolderID) {
            history.push(`/files/${recievedFolderID}`)
        } else if (onRefresh) {
            // Refresh the folder list if not navigating
            await onRefresh();
        }
        dispatch({ type: FILE_IMPORT_RESET });
        onClose()
    }
    if (result && !isSubmitting) {
        return (
            <div className="flex flex-col px-6 py-6 w-[700px] max-h-[90vh] overflow-hidden gap-6">
                {/* Header - static */}
                <div className="flex-shrink-0">
                    <h3 className="text-lg font-semibold">
                        {result.ok
                            ? result.rejectedCount > 0
                                ? t("Some files uploaded")
                                : t("Upload complete")
                            : t("Upload failed")}
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {result.message || error || ""}
                    </p>
                </div>

                {/* Scrollable content area */}
                <div className="flex-1 overflow-y-auto space-y-6 min-h-0">
                    {!!result.filesUploaded?.length && (
                        <div>
                            <div className="font-medium mb-3 text-gray-900 dark:text-gray-100">
                                {t("Uploaded")} ({result.filesUploaded.length})
                            </div>
                            <div className="space-y-2">
                                {result.filesUploaded.map((f: any) => (
                                    <div
                                        key={f.FileID}
                                        className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800"
                                    >
                                        <MdCheckCircle className="flex-shrink-0 h-5 w-5 text-green-600" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                                                {f.OriginalName}
                                            </p>
                                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                                {(f.Size / 1024).toFixed(1)} KB
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {!!result.filesRejected?.length && (
                        <div>
                            <div className="font-medium mb-3 text-gray-900 dark:text-gray-100">
                                {t("Rejected")} ({result.filesRejected.length})
                            </div>
                            <div className="space-y-2">
                                {result.filesRejected.map((r: any, i: any) => (
                                    <div
                                        key={i}
                                        className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800"
                                    >
                                        <div className="flex-shrink-0 h-5 w-5 text-red-600 mt-0.5">✕</div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                                                {r.OriginalName}
                                            </p>
                                            <p className="text-xs text-red-600 dark:text-red-400">
                                                {r.Reason}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer button - static */}
                <div className="flex-shrink-0 flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        type="button"
                        variant="primary"
                        size="small"
                        onClick={handleClose}
                    >
                        {t("Done")}
                    </Button>
                </div>
            </div>
        )
    }
    if (isSubmitting) {
        return (
            <div className="flex flex-col items-center justify-center w-[700px] max-h-[90vh]">
                <div className="flex flex-col items-center gap-4">
                    <div className="animate-spin">
                        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full"></div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">{t("Importing files...")}</p>
                </div>
            </div>
        )
    }



    return (
        <form onSubmit={formik.handleSubmit} className="flex flex-col w-[700px] max-h-[90vh] overflow-hidden">
            {/* Header section - not scrollable */}
            <div className="flex-shrink-0 px-6 pb-6 border-b border-gray-200 dark:border-gray-700 space-y-4">
                {/* Row 1: Toggle and Path Dropdown on same row */}
                <div className="flex items-start gap-4">
                    {/* Toggle */}
                    <div className="flex flex-col gap-4 flex-shrink-0">
                        <label className="block text-xs font-light text-gray-700 dark:text-gray-300">{t("Folder")}</label>
                        <div className="flex border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setToggle('existing')}
                                className={`px-2.5 py-1.5 text-xs font-light transition-colors ${toggle === 'existing'
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                            >
                                {t("Use existing")}
                            </button>
                            <button
                                type="button"
                                onClick={() => setToggle('new')}
                                className={`px-2.5 py-1.5 text-xs font-light transition-colors border-l border-gray-300 dark:border-gray-600 ${toggle === 'new'
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                            >
                                {t("Create new")}
                            </button>
                        </div>
                    </div>

                    {/* Path Dropdown - 60% width, always visible */}
                    <div className="flex flex-col flex-1">
                        <div className="flex items-center gap-1 mb-0">
                            <label className="block text-xs font-light text-gray-700 dark:text-gray-300">
                                {toggle === 'new' ? t("Parent Folder") : t("Path")}
                            </label>
                            {toggle === 'new' && (
                                <span className="text-gray-500 dark:text-gray-400 text-xs font-light">(optional)</span>
                            )}
                            <div
                                className="relative inline-flex items-center"
                                onMouseEnter={() => setShowPathTooltip(true)}
                                onMouseLeave={() => setShowPathTooltip(false)}
                            >
                                <button
                                    type="button"
                                    className="p-0.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                                    aria-label="Info"
                                >
                                    <PiInfo className="h-4 w-4" />
                                </button>
                                {showPathTooltip && (
                                    <div className="absolute left-6 top-1/2 -translate-y-1/2 z-50 w-48 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 text-xs text-gray-700 dark:text-gray-200 shadow-lg whitespace-normal">
                                        {toggle === 'new'
                                            ? t("Optionally select a parent folder for the new folder")
                                            : t('You can import files by selecting any folder.')
                                        }
                                        <div className="absolute -left-1 top-1/2 -translate-y-1/2 h-2 w-2 rotate-45 bg-white dark:bg-gray-800 border-l border-b border-gray-200 dark:border-gray-700" />
                                    </div>
                                )}
                            </div>
                        </div>
                        <Dropdown
                            options={folderOptions}
                            value={formik.values.FolderId}
                            onChange={(val) => {
                                formik.setFieldValue("FolderId", val);
                                setSelectedFolderId((Array.isArray(val) ? val[0] : val) || "");
                            }}
                            placeholder={t("Choose a folder")}
                            error={formik.touched.FolderId ? (formik.errors as any).FolderId : undefined}
                        />
                    </div>
                </div>

                {/* Row 2: Folder Name - only show for "Create new" mode */}
                {toggle === 'new' && (
                    <div className="flex flex-col gap-1">
                        <label className="block text-xs font-light text-gray-900 dark:text-gray-100">
                            {t("Folder Name")}
                        </label>
                        <Input
                            type="text"
                            name="FolderName"
                            value={formik.values.FolderName}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.FolderName
                                    ? (formik.errors as any).FolderName ||
                                    (formik.errors as any)?.["one-of-two"]
                                    : undefined
                            }
                            placeholder={t("e.g., Contracts 2025")}
                            className="rounded py-[10px] w-full placeholder-black hover:bg-gray-100 dark:hover:bg-gray-700 bg-white"
                        />
                    </div>
                )}
            </div>

            {/* Scrollable content section */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="space-y-4">
                    {!formik.values.FolderId &&
                        !formik.values.FolderName &&
                        (formik.errors as any)?.["one-of-two"] && (
                            <p className="text-sm font-light text-red-600">{(formik.errors as any)["one-of-two"]}</p>
                        )}

                    {/* Share with team members section - using unified CollaboratorSelector */}
                    <CollaboratorSelector
                        collaborators={collaborators}
                        onCollaboratorsChange={setCollaborators}
                        initialShared={selectedFolderId ? initialShared : []}
                    />

                    {/* File uploader section */}
                    <div>
                        <label className="block text-sm font-light text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("Import Files")}
                        </label>
                        <FileUploader files={formik.values.Files} onFilesChange={(newFiles: File[]) => formik.setFieldValue("Files", newFiles)} />
                        <p className="mt-2 text-xs font-light text-neutral-500">
                            {t("Unsupported files will be rejected automatically.")}
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer with button - not scrollable */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                <div className="flex justify-end">
                    <Button variant='primary' size='small' type="submit" disabled={isSubmitting}>
                        {
                            isSubmitting ? t('processing...') : t('Import')
                        }
                    </Button>
                </div>
            </div>
        </form>
    );
};

export default CreateFolder;
